import { Button } from '@components/primitives/Button.js'
import { Card } from '@components/primitives/Card.js'
import { EmptyPanel } from '@components/primitives/EmptyPanel.js'
import { NavIcon } from '@components/layout/NavIcons.js'
import { Money } from '@components/primitives/Money.js'
import { formatCurrency } from '@utils/format.js'
import { LancamentoRow } from './LancamentoRow.js'
import type { LancamentoItem, TipoFiltro, TipoLanc } from './financeiroTypes.js'
import { barFillStyle } from '@utils/barStyle.js'

type Props = {
  saldo: number
  orcamentoTotal: number
  totalGasto: number
  totalReceitas: number
  pctGasto: number
  tipoFiltro: TipoFiltro
  onTipoFiltroChange: (v: TipoFiltro) => void
  all: LancamentoItem[]
  filtered: LancamentoItem[]
  gastoPorCategoria: Record<string, number>
  orcamentoPorCategoria: Record<string, number>
  onNew: (tipo: TipoLanc) => void
  onEdit: (item: LancamentoItem) => void
  onDelete: (item: LancamentoItem) => void
}

export function LancamentosTab({
  saldo,
  orcamentoTotal,
  totalGasto,
  totalReceitas,
  pctGasto,
  tipoFiltro,
  onTipoFiltroChange,
  all,
  filtered,
  gastoPorCategoria,
  orcamentoPorCategoria,
  onNew,
  onEdit,
  onDelete,
}: Props) {
  return (
    <>
      <section className='cf3-block'>
        <Card surface='raised' className='cf3-fin-saldo-card'>
          <div className='cf3-fin-saldo-card__header'>
            <span className='cf-label'>Saldo da obra</span>
            {orcamentoTotal > 0 && (
              <span
                className={`cf3-fin-saude cf3-fin-saude--${pctGasto >= 90 ? 'critico' : pctGasto >= 70 ? 'atencao' : 'ok'}`}
              >
                {pctGasto}% usado
              </span>
            )}
          </div>
          <div className='cf3-fin-value-block'>
            <Money
              value={saldo}
              size={28}
              color={saldo < 0 ? 'var(--cf-color-danger-600)' : 'var(--cf-fg-primary)'}
              ariaLabel={`Saldo: ${formatCurrency(saldo)}`}
            />
          </div>
          {orcamentoTotal > 0 && (
            <>
              <div
                className='cf3-fin-budget-bar cf3-fin-budget-bar--spaced'
                role='progressbar'
                aria-valuenow={pctGasto}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${pctGasto}% do orçamento utilizado`}
              >
                <div
                  className={`cf3-fin-budget-bar__fill${pctGasto >= 90 ? ' cf3-fin-budget-bar__fill--danger' : pctGasto >= 70 ? ' cf3-fin-budget-bar__fill--warning' : ''}`}
                  style={barFillStyle(pctGasto)}
                />
              </div>
              <div className='cf3-fin-saldo-footer'>
                <span>Gasto: {formatCurrency(totalGasto)}</span>
                <span>Orçado: {formatCurrency(orcamentoTotal)}</span>
              </div>
            </>
          )}
        </Card>

        <div className='cf3-fin-minicards'>
          <Card>
            <p className='cf-label cf3-overline--gasto'>Gastos</p>
            <div className='cf3-fin-value-block'>
              <Money
                value={totalGasto}
                size={20}
                color='var(--cf-color-danger-600)'
                ariaLabel={`Total gasto: ${formatCurrency(totalGasto)}`}
              />
            </div>
          </Card>
          <Card>
            <p className='cf-label cf3-overline--receita'>Receitas</p>
            <div className='cf3-fin-value-block'>
              <Money
                value={totalReceitas}
                size={20}
                color='var(--cf-color-success-600)'
                ariaLabel={`Total receitas: ${formatCurrency(totalReceitas)}`}
              />
            </div>
          </Card>
        </div>
      </section>

      <section className='cf3-block'>
        <div className='cf3-fin-toolbar'>
          <div className='cf3-chip-row' role='group' aria-label='Filtrar por tipo'>
            {(
              [
                ['todos', 'Todos'],
                ['gasto', 'Gastos'],
                ['receita', 'Receitas'],
              ] as const
            ).map(([val, label]) => (
              <button
                key={val}
                className={`cf3-chip${tipoFiltro === val ? ' cf3-chip--active' : ''}`}
                onClick={() => onTipoFiltroChange(val)}
                aria-pressed={tipoFiltro === val}
              >
                {label}
              </button>
            ))}
          </div>
          <div className='cf3-fin-toolbar__btns'>
            <Button
              variant='primary'
              size='sm'
              onClick={() => onNew('gasto')}
              aria-label='Registrar novo gasto'
            >
              + Gasto
            </Button>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => onNew('receita')}
              aria-label='Registrar nova receita'
            >
              + Receita
            </Button>
          </div>
        </div>
      </section>

      <section className='cf3-block'>
        {filtered.length === 0 ? (
          all.length === 0 ? (
            <EmptyPanel
              icon={<NavIcon icon='financeiro' size={24} />}
              title='Nenhum lançamento'
              description='Registre gastos e receitas para acompanhar o saldo da obra e comparar com o orçamento.'
              actionLabel='Registrar gasto'
              onAction={() => onNew('gasto')}
              secondaryActionLabel='Registrar receita'
              onSecondaryAction={() => onNew('receita')}
            />
          ) : (
            <EmptyPanel
              title='Nenhum resultado'
              description='Nenhum lançamento corresponde a este filtro.'
              actionLabel='Ver todos'
              onAction={() => onTipoFiltroChange('todos')}
            />
          )
        ) : (
          <div className='cf3-lancamento-list' aria-label='Lista de lançamentos'>
            {filtered.map((item) => (
              <LancamentoRow
                key={item.id}
                item={item}
                onEdit={() => onEdit(item)}
                onDelete={() => onDelete(item)}
              />
            ))}
          </div>
        )}
      </section>

      {Object.keys(gastoPorCategoria).length > 0 && (
        <section className='cf3-block' aria-labelledby='cat-heading'>
          <h3 id='cat-heading' className='cf3-subsection-title'>
            Gastos por Categoria
          </h3>
          <Card surface='raised'>
            <ul className='cf3-fin-cat-list'>
              {Object.entries(gastoPorCategoria)
                .sort(([, a], [, b]) => b - a)
                .map(([cat, valor]) => {
                  const budget = orcamentoPorCategoria[cat] ?? 0
                  const pct = budget > 0 ? Math.min(Math.round((valor / budget) * 100), 100) : null
                  return (
                    <li key={cat} className='cf3-fin-cat-item'>
                      <div className='cf3-fin-cat-item__row'>
                        <span className='cf3-fin-cat-item__nome'>{cat}</span>
                        <span className='cf3-fin-cat-item__valor'>{formatCurrency(valor)}</span>
                      </div>
                      {pct !== null && (
                        <div
                          className='cf3-fin-cat-bar'
                          role='progressbar'
                          aria-valuenow={pct}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-label={`${pct}% do orçamento de ${cat}`}
                        >
                          <div
                            className={`cf3-fin-cat-bar__fill${pct >= 90 ? ' cf3-fin-cat-bar__fill--danger' : pct >= 70 ? ' cf3-fin-cat-bar__fill--warning' : ''}`}
                            style={barFillStyle(pct)}
                          />
                        </div>
                      )}
                    </li>
                  )
                })}
            </ul>
          </Card>
        </section>
      )}
    </>
  )
}
